import type { CSSProperties, ReactNode } from "react";

import { cn, hasRenderableNode } from "../base/utils";
import { NavigationSidebar } from "../navigation-sidebar";
import type { WorkbenchVisualizationTone } from "./WorkbenchScatterPlot";
import {
  workbenchVisualizationToneColor,
  workbenchVisualizationToneColorWithAlpha,
} from "./workbenchVisualizationTone";

export interface WorkbenchRadarNavigationChild {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
}

export interface WorkbenchRadarNavigationItem {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  children?: WorkbenchRadarNavigationChild[];
}

export interface WorkbenchRadarWorkspaceProps {
  navigationItems?: WorkbenchRadarNavigationItem[];
  activeNavigationId?: string;
  activeNavigationChildId?: string;
  onNavigationSelect?: (item: WorkbenchRadarNavigationItem) => void;
  onNavigationChildSelect?: (
    child: WorkbenchRadarNavigationChild,
    parent: WorkbenchRadarNavigationItem,
  ) => void;
  navigationFooter?: ReactNode;
  navigation?: ReactNode;
  children: ReactNode;
  inspector?: ReactNode;
  inspectorLabel?: string;
  className?: string;
  contentClassName?: string;
  minimumHeight?: string | number;
  ariaLabel?: string;
}

const radarWorkspaceStyle: CSSProperties = {
  backgroundColor: "hsl(var(--radar-canvas, var(--background)))",
  color: "hsl(var(--foreground))",
};

const radarPanelStyle: CSSProperties = {
  backgroundColor: "hsl(var(--radar-panel, var(--card)))",
  borderColor: "hsl(var(--radar-border, var(--border)))",
};

export interface WorkbenchRadarNavigationProps {
  items: WorkbenchRadarNavigationItem[];
  activeItemId?: string;
  activeChildId?: string;
  onItemSelect?: (item: WorkbenchRadarNavigationItem) => void;
  onChildSelect?: (
    child: WorkbenchRadarNavigationChild,
    parent: WorkbenchRadarNavigationItem,
  ) => void;
  footer?: ReactNode;
  ariaLabel?: string;
}

export function WorkbenchRadarNavigation({
  items,
  activeItemId,
  activeChildId,
  onItemSelect,
  onChildSelect,
  footer,
  ariaLabel = "Radar navigation",
}: WorkbenchRadarNavigationProps) {
  return (
    <NavigationSidebar
      kind="route"
      routeItems={items.map((item) => ({
        ...item,
        children: item.children?.map((child) => ({
          ...child,
          domProps: { "data-radar-navigation-child-id": child.id },
        })),
      }))}
      activeItemId={activeItemId}
      activeChildId={activeChildId}
      onItemSelect={
        onItemSelect
          ? (item) => onItemSelect(item as WorkbenchRadarNavigationItem)
          : undefined
      }
      onChildSelect={
        onChildSelect
          ? (child, parent) =>
              onChildSelect(
                child as WorkbenchRadarNavigationChild,
                parent as WorkbenchRadarNavigationItem,
              )
          : undefined
      }
      footer={footer}
      ariaLabel={ariaLabel}
      dataComponent="workbench-radar-navigation"
      widthMode="fill"
      resizable={false}
      collapsible={false}
      appearance={{
        density: "default",
        surface: "outlined",
        activeVariant: "solid",
      }}
      className="min-w-0 self-stretch rounded-lg"
      classNames={{
        content: "p-3",
        list: "gap-2",
        item: "min-h-11 px-3 py-2 text-sm font-semibold",
        itemActive:
          "bg-primary text-primary-foreground shadow-[0_0_1.25rem_hsl(var(--primary)/0.28)]",
        childList: "ml-5 gap-0.5 border-l border-border/70 py-1 pl-3",
        childItem: "min-h-9 px-2 text-xs font-medium",
        childItemActive: "border-primary/40 bg-primary/15 text-primary",
        footer: "p-3",
      }}
      style={radarPanelStyle}
    />
  );
}

export function WorkbenchRadarWorkspace({
  navigationItems = [],
  activeNavigationId,
  activeNavigationChildId,
  onNavigationSelect,
  onNavigationChildSelect,
  navigationFooter,
  navigation,
  children,
  inspector,
  inspectorLabel = "Context controls",
  className,
  contentClassName,
  minimumHeight = "calc(100dvh - 1rem)",
  ariaLabel = "Radar workspace",
}: WorkbenchRadarWorkspaceProps) {
  return (
    <section
      data-monkeys-component="workbench-radar-workspace"
      data-radar-active-navigation-id={activeNavigationId}
      aria-label={ariaLabel}
      className={cn(
        "grid min-w-0 gap-3 overflow-hidden p-3 lg:grid-cols-[12.5rem_minmax(0,1fr)] xl:grid-cols-[12.5rem_minmax(0,1fr)_20rem]",
        className,
      )}
      style={{ ...radarWorkspaceStyle, minHeight: minimumHeight }}
    >
      {hasRenderableNode(navigation) ? (
        navigation
      ) : (
        <WorkbenchRadarNavigation
          items={navigationItems}
          activeItemId={activeNavigationId}
          activeChildId={activeNavigationChildId}
          onItemSelect={onNavigationSelect}
          onChildSelect={onNavigationChildSelect}
          footer={navigationFooter}
        />
      )}

      <div
        data-radar-workspace-region="content"
        className={cn(
          "relative min-h-[42rem] min-w-0 overflow-hidden rounded-lg border",
          contentClassName,
        )}
        style={{
          backgroundColor: "hsl(var(--radar-canvas, var(--background)))",
          borderColor: "hsl(var(--radar-shell-border, var(--border)))",
          boxShadow:
            "inset 0 0 0 1px hsl(var(--radar-grid, var(--border)) / 0.22)",
        }}
      >
        {children}
      </div>

      {hasRenderableNode(inspector) ? (
        <aside
          aria-label={inspectorLabel}
          data-radar-workspace-region="inspector"
          className="min-h-0 overflow-y-auto rounded-lg border lg:col-span-2 xl:col-span-1"
          style={radarPanelStyle}
        >
          {inspector}
        </aside>
      ) : null}
    </section>
  );
}

export function WorkbenchRadarInspectorSection({
  title,
  icon,
  description,
  children,
  className,
}: {
  title: ReactNode;
  icon?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <section
      data-monkeys-component="workbench-radar-inspector-section"
      className={cn("border-b border-border/70 p-4 last:border-b-0", className)}
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-primary">
        {hasRenderableNode(icon) ? (
          <span aria-hidden="true">{icon}</span>
        ) : null}
        <h2>{title}</h2>
      </div>
      {hasRenderableNode(description) ? (
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          {description}
        </p>
      ) : null}
      {hasRenderableNode(children) ? (
        <div className="mt-3">{children}</div>
      ) : null}
    </section>
  );
}

export interface WorkbenchRadarFilterRowDisclosureBase {
  expanded: boolean;
  controls: string;
  expandLabel: string;
  collapseLabel: string;
  onToggle: () => void;
}

export interface WorkbenchRadarFilterRowIntegratedDisclosure extends WorkbenchRadarFilterRowDisclosureBase {
  mode: "integrated";
}

export interface WorkbenchRadarFilterRowSeparateDisclosure extends WorkbenchRadarFilterRowDisclosureBase {
  mode: "separate";
}

interface WorkbenchRadarFilterRowBaseProps {
  label: ReactNode;
  value?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  tone?: WorkbenchVisualizationTone;
  className?: string;
}

export type WorkbenchRadarFilterRowProps = WorkbenchRadarFilterRowBaseProps &
  (
    | {
        onSelect: () => void;
        selected?: boolean;
        disclosure?: never;
      }
    | {
        onSelect?: never;
        selected?: never;
        disclosure: WorkbenchRadarFilterRowIntegratedDisclosure;
      }
    | {
        onSelect: () => void;
        selected?: boolean;
        disclosure: WorkbenchRadarFilterRowSeparateDisclosure;
      }
  );

export interface WorkbenchRadarFilterChipProps {
  label: ReactNode;
  value?: ReactNode;
  icon?: ReactNode;
  selected?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  className?: string;
}

type WorkbenchRadarFilterStyle = CSSProperties & {
  "--workbench-radar-filter-tone"?: string;
  "--tw-ring-color"?: string;
};

function assertValidRadarFilterRowProps(props: WorkbenchRadarFilterRowProps) {
  const { disclosure, onSelect } = props;
  const invalid =
    (!disclosure && !onSelect) ||
    (disclosure?.mode === "integrated" &&
      (Boolean(onSelect) || props.selected !== undefined)) ||
    (disclosure?.mode === "separate" && !onSelect);

  if (invalid) {
    throw new Error("Invalid WorkbenchRadarFilterRow props.");
  }
}

export function WorkbenchRadarFilterRow(props: WorkbenchRadarFilterRowProps) {
  assertValidRadarFilterRowProps(props);

  const {
    label,
    value,
    icon,
    selected,
    disabled = false,
    tone,
    onSelect,
    disclosure,
    className,
  } = props;
  const isSelected = selected ?? false;
  const selectedClasses = isSelected
    ? undefined
    : "monkeys-workbench-radar-filter-row--idle";
  const disabledClasses = disabled
    ? "monkeys-workbench-radar-filter-disabled"
    : undefined;
  const resolvedTone = tone ?? "primary";
  const toneColor = workbenchVisualizationToneColor(resolvedTone);
  const style: WorkbenchRadarFilterStyle = {
    "--workbench-radar-filter-tone": toneColor,
    "--tw-ring-color": tone ? toneColor : "hsl(var(--ring))",
    ...(isSelected || tone
      ? {
          backgroundColor: workbenchVisualizationToneColorWithAlpha(
            resolvedTone,
            isSelected ? 0.14 : 0.08,
          ),
          borderColor: toneColor,
        }
      : {}),
  };
  const content = (
    <>
      {hasRenderableNode(icon) ? (
        <span
          className={cn(
            "flex size-4 shrink-0 items-center justify-center",
            tone
              ? "text-[var(--workbench-radar-filter-tone)]"
              : "text-muted-foreground",
          )}
          aria-hidden="true"
        >
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 truncate">{label}</span>
      {hasRenderableNode(value) ? (
        <span className="ml-auto shrink-0 tabular-nums text-muted-foreground">
          {value}
        </span>
      ) : null}
    </>
  );
  const disclosureIcon = disclosure ? (
    <span
      aria-hidden="true"
      className={cn(
        "text-lg leading-none transition-transform motion-reduce:transition-none",
        disclosure.expanded && "rotate-90",
      )}
    >
      ›
    </span>
  ) : null;

  if (disclosure?.mode === "separate") {
    const disclosureLabel = disclosure.expanded
      ? disclosure.collapseLabel
      : disclosure.expandLabel;

    return (
      <div
        data-monkeys-component="workbench-radar-filter-row"
        className={cn(
          "monkeys-workbench-radar-filter-row monkeys-workbench-radar-filter-row--separate",
          selectedClasses,
          disabledClasses,
          className,
        )}
        style={style}
      >
        <button
          type="button"
          aria-pressed={selected}
          disabled={disabled}
          onClick={onSelect}
          className={cn(
            "monkeys-workbench-radar-filter-control monkeys-workbench-radar-filter-row__action",
            disabledClasses,
          )}
        >
          {content}
        </button>
        <button
          type="button"
          aria-expanded={disclosure.expanded}
          aria-controls={disclosure.controls}
          aria-label={disclosureLabel}
          disabled={disabled}
          onClick={disclosure.onToggle}
          className={cn(
            "monkeys-workbench-radar-filter-row__disclosure",
            disabledClasses,
          )}
        >
          {disclosureIcon}
        </button>
      </div>
    );
  }

  const integratedDisclosure =
    disclosure?.mode === "integrated" ? disclosure : undefined;
  const disclosureLabel = integratedDisclosure
    ? integratedDisclosure.expanded
      ? integratedDisclosure.collapseLabel
      : integratedDisclosure.expandLabel
    : undefined;

  return (
    <button
      type="button"
      data-monkeys-component="workbench-radar-filter-row"
      aria-label={disclosureLabel}
      aria-pressed={integratedDisclosure ? undefined : selected}
      aria-expanded={integratedDisclosure?.expanded}
      aria-controls={integratedDisclosure?.controls}
      disabled={disabled}
      onClick={integratedDisclosure?.onToggle ?? onSelect}
      className={cn(
        "monkeys-workbench-radar-filter-control monkeys-workbench-radar-filter-row",
        selectedClasses,
        disabledClasses,
        className,
      )}
      style={style}
    >
      {content}
      {integratedDisclosure ? (
        <span className="ml-auto shrink-0 text-muted-foreground">
          {disclosureIcon}
        </span>
      ) : null}
    </button>
  );
}

export function WorkbenchRadarFilterChip({
  label,
  value,
  icon,
  selected = false,
  disabled = false,
  onSelect,
  className,
}: WorkbenchRadarFilterChipProps) {
  const interactive = Boolean(onSelect) || disabled;
  const Comp = interactive ? "button" : "div";
  return (
    <Comp
      type={interactive ? "button" : undefined}
      aria-pressed={onSelect && !disabled ? selected : undefined}
      disabled={interactive ? disabled : undefined}
      onClick={onSelect}
      className={cn(
        "flex min-w-0 items-center border text-xs font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none",
        "monkeys-workbench-radar-filter-control rounded-full",
        selected
          ? "border-primary bg-primary/20 text-primary"
          : "border-border bg-background/40 text-foreground hover:border-primary/60 hover:bg-primary/10",
        disabled && "cursor-not-allowed opacity-50",
        className,
      )}
    >
      {hasRenderableNode(icon) ? (
        <span className="shrink-0" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 truncate">{label}</span>
      {hasRenderableNode(value) ? (
        <span className="ml-auto shrink-0 text-muted-foreground">{value}</span>
      ) : null}
    </Comp>
  );
}
